import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useAction } from "wasp/client/operations";
import { getEventById, rsvpToEvent, deleteEvent } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Users,
  User,
  Edit3,
  Trash2,
  Eye,
  Share2,
  UserPlus,
  Video,
  Globe,
  AlertTriangle,
  ExternalLink,
} from "lucide-react";
import { SubscribeButton } from "../content/components/SubscribeButton";
import {
  IndividualViewPageLayout,
  SidebarSection,
  ActionButtons,
  DetailField,
  ActionButtonLink,
  ActionButtonDanger,
} from "../../components/layout";
import { DashboardLayout } from "../../client/components/Sidebar";

export default function ViewEventPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { data: user } = useAuth();
  const navigate = useNavigate();

  // Fetch event data
  const {
    data: event,
    isLoading,
    error,
  } = useQuery(getEventById, { eventId: eventId! });

  const rsvpAction = useAction(rsvpToEvent);
  const deleteEventAction = useAction(deleteEvent);

  const formatEventDateTime = (
    startTime: string,
    endTime: string,
    timezone: string,
  ) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const sameDay = start.toDateString() === end.toDateString();

    const dateOptions: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };

    if (sameDay) {
      return {
        date: start.toLocaleDateString("en-US", dateOptions),
        time: `${start.toLocaleTimeString(
          "en-US",
          timeOptions,
        )} - ${end.toLocaleTimeString("en-US", timeOptions)}`,
        timezone: timezone,
      };
    } else {
      return {
        date: `${start.toLocaleDateString(
          "en-US",
          dateOptions,
        )} - ${end.toLocaleDateString("en-US", dateOptions)}`,
        time: `${start.toLocaleTimeString(
          "en-US",
          timeOptions,
        )} - ${end.toLocaleTimeString("en-US", timeOptions)}`,
        timezone: timezone,
      };
    }
  };

  const handleRSVP = async () => {
    if (!eventId) return;

    try {
      await rsvpAction({ eventId });
      // Refresh the page to show updated attendance
      window.location.reload();
    } catch (error: any) {
      alert(error.message || "Failed to RSVP to event");
    }
  };

  const isUserRegistered = () => {
    if (!user || !event?.attendees) return false;
    return event.attendees.some(
      (attendee: any) => attendee.user.id === user.id,
    );
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Authentication Required
            </h2>
            <p className="text-gray-600">
              You must be logged in to view events.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading event...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !event) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Event Not Found
            </h3>
            <p className="text-gray-600 mb-4">
              The event you're looking for doesn't exist or has been removed.
            </p>
            <button
              onClick={() => navigate("/events")}
              className="bg-blue-600 text-white px-4 py-2 hover:bg-blue-700 transition-colors"
            >
              Back to Events
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const isAdmin = user?.isAdmin;
  const dateTime = formatEventDateTime(
    event.startTime,
    event.endTime,
    event.timezone,
  );
  const isRegistered = isUserRegistered();
  const eventDate = new Date(event.startTime);
  const isEventPast = eventDate < new Date();
  const canRegister =
    !isRegistered && !isEventPast && event.requiresRegistration;

  const currentPageUrl =
    typeof window !== "undefined" ? window.location.href : "";

  const breadcrumbItems = [
    {
      label: "About",
      dropdownItems: [
        { label: "Notices", href: "/notices" },
        { label: "Events/Activities", href: "/events" },
        { label: "Tenants", href: "/tenants" },
        { label: "Facilities", href: "/facilities" },
      ],
    },
    {
      label: "Events/Activities",
      isClickable: true,
      href: "/events",
    },
  ];

  const actionsContent = (
    <ActionButtons>
      {isAdmin && (
        <>
          <ActionButtonLink to={`/events/${eventId}/edit`} icon={Edit3}>
            Edit Event
          </ActionButtonLink>
          <ActionButtonDanger
            onClick={async () => {
              if (confirm("Are you sure you want to delete this event?")) {
                try {
                  await deleteEventAction({ eventId: eventId! });
                  navigate("/events");
                } catch (error: any) {
                  alert(error.message || "Failed to delete event");
                }
              }
            }}
            icon={Trash2}
          >
            Delete Event
          </ActionButtonDanger>
        </>
      )}

      {event.requiresRegistration && (
        <>
          {isRegistered ? (
            <div className="w-full text-center p-3 bg-green-50 border border-green-100 rounded-xl">
              <p className="font-bold text-green-700 flex items-center justify-center gap-2">
                <UserPlus className="w-4 h-4" /> Registered
              </p>
            </div>
          ) : isEventPast ? (
            <div className="w-full text-center p-3 bg-slate-100 rounded-xl">
              <p className="font-bold text-slate-500 italic">Event Ended</p>
            </div>
          ) : canRegister ? (
            <button
              onClick={handleRSVP}
              disabled={event.maxAttendees ? (event.attendees?.length || 0) >= event.maxAttendees : false}
              className="w-full bg-blue-600 text-white px-4 py-3 rounded-xl font-bold hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserPlus className="w-4 h-4" />
              {event.maxAttendees && (event.attendees?.length || 0) >= event.maxAttendees ? "Event Full" : "Register Now"}
            </button>
          ) : null}
        </>
      )}
    </ActionButtons>
  );

  const sidebarContent = (
    <>

      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField label="Event Type" value={event.eventType || "Event"} />
          <DetailField label="Status" value={event.status || "N/A"} />
          <DetailField label="Visibility" value={event.visibility || "N/A"} />
          <DetailField
            label="Created"
            value={new Date(event.createdAt).toLocaleDateString()}
          />
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={breadcrumbItems}
      currentPageUrl={currentPageUrl}
      sidebarContent={sidebarContent}
      actions={actionsContent}
    >
      {/* Event Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{event.title}</h1>
            <p className="text-gray-600">
              Organized by {event.organizer.username || event.organizer.email}
            </p>
          </div>
          <SubscribeButton contentId={event.id} />
        </div>
      </div>
      {/* Cover Image */}
      {event.coverImage && (
        <div className="mb-6">
          <img
            src={event.coverImage}
            alt={event.title}
            className="w-full h-64 object-cover border border-gray-200"
          />
        </div>
      )}

      {/* Event Status */}
      {isEventPast && (
        <div className="bg-gray-50 border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-700">
              This event has ended
            </span>
          </div>
        </div>
      )}

      {/* Event Details */}
      <div className="bg-white border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Event Details
        </h2>

        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-gray-500 mt-0.5" />
            <div>
              <div className="font-medium text-gray-900">{dateTime.date}</div>
              <div className="text-gray-600">{dateTime.time}</div>
              <div className="text-sm text-gray-500">{dateTime.timezone}</div>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-gray-500 mt-0.5" />
            <div>
              <div className="font-medium text-gray-900">
                {event.isVirtual ? "Virtual Event" : event.venueName || "TBD"}
              </div>
              {event.isVirtual && event.meetingUrl && (
                <a
                  href={event.meetingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 flex items-center gap-1 mt-1"
                >
                  <Video className="w-4 h-4" />
                  Join Meeting
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {!event.isVirtual && event.venueAddress && (
                <div className="text-gray-600 mt-1">{event.venueAddress}</div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-gray-500" />
            <div>
              <span className="font-medium text-gray-900">
                {event.attendees?.length || 0} attending
              </span>
              {event.maxAttendees && (
                <span className="text-gray-600">
                  {" "}
                  (max {event.maxAttendees})
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-gray-500" />
            <div>
              <span className="font-medium text-gray-900">
                Organized by {event.organizer.username || event.organizer.email}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="bg-white border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          About This Event
        </h2>
        <div className="prose max-w-none">
          <p className="text-gray-700 whitespace-pre-wrap">
            {event.description}
          </p>
        </div>
      </div>

      {/* Attendees */}
      {event.attendees && event.attendees.length > 0 && (
        <div className="bg-white border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Attendees ({event.attendees.length})
          </h2>
          <div className="space-y-3">
            {event.attendees.map((attendee: any) => (
              <div key={attendee.id} className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">
                    {attendee.user.username || attendee.user.email}
                  </div>
                  <div className="text-sm text-gray-500">
                    Registered{" "}
                    {new Date(attendee.registeredAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </IndividualViewPageLayout>
  );
}
